// ═══════════════════════════════════════════════════════════════════════════
// photoExtract.js — send ONE photo to the extract service and get JSON back.
//
// This was inline in the basis-sheet importer and is now called by two readers
// (the basis sheet and the handwritten target ticket). Every line of it is a
// measured failure someone already paid for, and a second copy would mean the
// next fix lands in one of them:
//
//   • A THROWN fetch is retried; an HTTP ERROR is not. A throw means no
//     response came back at all — a dropped upload, a proxy, a flaky link —
//     which is transient by nature. An HTTP status is the service ANSWERING,
//     and hammering it just repeats the same answer. The browser upload path
//     was dying with a bare network error on 2026-08-24/25 while the identical
//     POST from Node succeeded, which is why the retry exists at all.
//   • The response is read as TEXT first. A gateway 5xx returns an HTML page,
//     and JSON.parse on it throws something that reads like a parser bug
//     rather than an outage.
//   • The JSON is sliced between the first { and the last }. Models wrap
//     output in prose often enough that treating the whole body as JSON is a
//     failure mode, not an edge case.
//
// It is DOM-free on purpose — the caller does the canvas downscale and passes
// base64 — so it can be exercised offline against a mocked transport.
// ═══════════════════════════════════════════════════════════════════════════

const EXTRACT_MODEL = "claude-opus-4-8";
const EXTRACT_MAX_TOKENS = 16000;
const MAX_ATTEMPTS = 3;

// Build the request body. Separate so a checker can assert its shape without
// standing up a transport.
function buildExtractBody({ b64, media, prompt, model, maxTokens }) {
  return {
    model: model || EXTRACT_MODEL,
    max_tokens: maxTokens || EXTRACT_MAX_TOKENS,
    messages: [{
      role: "user",
      content: [
        { type: "image", source: { type: "base64", media_type: media || "image/jpeg", data: b64 } },
        { type: "text", text: prompt },
      ],
    }],
  };
}

// Pull the JSON object out of a model response. Throws with a message that
// says which stage failed — "no JSON found" and "the model returned no text"
// need different fixes and the old single message could not tell them apart.
function parseModelJson(responseText) {
  let j = null;
  try { j = JSON.parse(responseText); } catch (_) { /* handled below */ }
  if (!j || j.error) throw new Error(j && j.error && j.error.message
    ? j.error.message : "no response from the extract service");
  const text = (j.content && j.content[0] && j.content[0].text) || "";
  if (!text) throw new Error("the model returned no text");
  const first = text.indexOf("{"), last = text.lastIndexOf("}");
  if (first < 0 || last < 0) throw new Error("no JSON found in the model response");
  return JSON.parse(text.slice(first, last + 1));
}

/**
 * @param {object}   o
 * @param {string}   o.b64      base64 image payload (no data: prefix)
 * @param {string}   o.media    e.g. "image/jpeg"
 * @param {string}   o.prompt   the instruction for this document type
 * @param {function} o.apiFetch (url, init) => Response — injected, so this
 *                              module never reaches for a global
 * @param {function} [o.onStatus] called with a human sentence, or null to clear
 * @returns {Promise<object>} the parsed JSON object
 */
async function extractPhotoJson({ b64, media, prompt, apiFetch, onStatus, model, maxTokens }) {
  const say = typeof onStatus === "function" ? onStatus : () => {};
  const payload = JSON.stringify(buildExtractBody({ b64, media, prompt, model, maxTokens }));

  let res = null, lastNetErr = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      say(attempt === 1 ? null : `Connection dropped — retrying (${attempt} of ${MAX_ATTEMPTS})…`);
      res = await apiFetch("/api/extract", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: payload,
      });
      lastNetErr = null;
      break;
    } catch (netEx) {
      lastNetErr = netEx;
      if (attempt === MAX_ATTEMPTS) break;
      await new Promise((r) => setTimeout(r, attempt * 1500));
    }
  }
  if (!res) {
    throw Object.assign(lastNetErr || new Error("the request never reached the extract service"),
                        { _tries: MAX_ATTEMPTS });
  }
  say(null);

  const raw = await res.text();
  if (!res.ok) {
    let j = null; try { j = JSON.parse(raw); } catch (_) {}
    throw new Error(`extract service ${res.status}` + (j && j.error && j.error.message
      ? " — " + j.error.message : (raw ? " — " + String(raw).slice(0, 120) : "")));
  }
  return parseModelJson(raw);
}

module.exports = {
  EXTRACT_MODEL, EXTRACT_MAX_TOKENS, MAX_ATTEMPTS,
  buildExtractBody, parseModelJson, extractPhotoJson,
};
